import { dividerProbes, parcelGeom } from "./placement";
import type { Pt } from "./placement";
import { parcelFill, UNASSIGNED_FILL } from "../map/color";
import { badgeInfos } from "../map/filters";
import type { BadgeInfo } from "../map/filters";
import type { ApiLocation, Category, CategoryStyle, MapData } from "../map/model";

export interface ViewBoxRect { x: number; y: number; width: number; height: number; }

/**
 * İç dikişi kapatan çizginin kalınlığı (SVG kullanıcı birimi). `split-parcels`
 * parselleri ~1.6 birimlik ayraçla kesiyor; 2.2 bunu iki yanda ~0.3 payla
 * örter. Taşan pay dikişin iki yanındaki parsele denk geldiği ve aynı renge
 * boyandığı için görünmez.
 */
const SEAM_WIDTH = 2.2;

/** Bir rozet slotundaki tek bir rozet (kat + kategori) ve DOM karşılığı. */
export interface BadgeTarget { info: BadgeInfo; el: SVGGElement }

/**
 * Filtrenin üzerinde çalıştığı hedefler. Filtre artık `data-cats`/`data-levels`
 * gibi serileştirilmiş string'lere değil, doğrudan lokasyon MODELİNE bakar —
 * kategori ve kat koşulu aynı ünite üzerinde birlikte değerlendirilebilsin diye.
 */
export interface FilterTargets {
  parcels: { loc: ApiLocation; el: SVGPathElement }[];
  /**
   * Tek lokasyona bağlı komşu parsellerin arasındaki İÇ dikişler. Parseller
   * ayraç genişliği kadar boşlukla kesildiği için sadece ayraç çizgisini
   * gizlemek yetmez — dikiş, parsellerle aynı renge boyanarak kapatılır ve
   * filtre değiştikçe parsellerle birlikte yeniden boyanır.
   */
  seams: { loc: ApiLocation; el: SVGPathElement }[];
  /** Her slot = tek görünür rozet (gruplu lokasyon → 1 slot, grupsuz → parsel başına). */
  badgeSlots: { badges: BadgeTarget[] }[];
}

export interface SvgMap {
  svg: SVGSVGElement;
  getLocation(id: string): ApiLocation | undefined;
  parcelCount: number;
  /** Gezilebilir sınır (pan/zoom kilidi) — alt kara, deniz sınırlı */
  bounds: ViewBoxRect;
  filterTargets: FilterTargets;
}

/**
 * public/map.svg'yi sayfaya gömer. Bu SVG, kullanıcının PNG tabanını
 * (<image>, binalar hariç her şey) + ayraç çizgilerine göre bölünmüş
 * tıklanabilir parsel birimlerini (.parcel[data-id]) içerir. Üretici:
 * tools/split-parcels.mjs. Her parsel, `svgParcelId` alanı bu parsele denk
 * gelen (varsa) bir Location ile eşleşirse API kategorisine/özel rengine
 * göre boyanır ve TEK bir rozet alır (mapRefCode, yoksa baş harfler).
 */
export async function createSvgMap(container: HTMLElement, data: MapData, url = "/map.svg"): Promise<SvgMap> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`map.svg yüklenemedi: ${res.status}`);
  container.innerHTML = await res.text();

  const svg = container.querySelector("svg") as SVGSVGElement | null;
  if (!svg) throw new Error("map.svg kök elementi bulunamadı");

  const stylesByCategory = new Map<Category, CategoryStyle>(data.categoryStyles.map((s) => [s.category, s]));
  const locById = new Map<string, ApiLocation>(data.locations.map((l) => [l.id, l]));
  // Bir lokasyonun kapsadığı TÜM üye parseller (birincil + ek), benzersiz.
  const membersOf = (loc: ApiLocation): string[] =>
    [...new Set([loc.svgParcelId, ...(loc.svgParcelIds ?? [])].filter((x): x is string => !!x))];

  const locByParcel = new Map<string, ApiLocation>();
  for (const loc of data.locations) {
    for (const pid of membersOf(loc)) locByParcel.set(pid, loc);
  }

  const SVGNS = "http://www.w3.org/2000/svg";
  const parcels = svg.querySelectorAll<SVGPathElement>(".parcel");

  // Dairesel logo kırpması (rozet içindeki logo görselleri için)
  const defs = document.createElementNS(SVGNS, "defs");
  const clip = document.createElementNS(SVGNS, "clipPath");
  clip.setAttribute("id", "badge-logo-clip");
  clip.setAttribute("clipPathUnits", "objectBoundingBox");
  const clipCircle = document.createElementNS(SVGNS, "circle");
  clipCircle.setAttribute("cx", "0.5"); clipCircle.setAttribute("cy", "0.5"); clipCircle.setAttribute("r", "0.5");
  clip.appendChild(clipCircle); defs.appendChild(clip); svg.appendChild(defs);

  // Rozet katmanı (parsellerin üstünde; tıklanabilir)
  const logoLayer = document.createElementNS(SVGNS, "g");
  logoLayer.setAttribute("id", "logos");
  svg.appendChild(logoLayer);

  // Parsel poligonunu örnekle (köşe noktaları) — eğik/ince şeritlerde bbox
  // yanıltıcı; gerçek şekle göre yerleşim için noktalar gerekir.
  function samplePath(path: SVGPathElement, count: number): { x: number; y: number }[] {
    const total = path.getTotalLength();
    if (!total) {
      const b = path.getBBox();
      return [
        { x: b.x, y: b.y }, { x: b.x + b.width, y: b.y },
        { x: b.x + b.width, y: b.y + b.height }, { x: b.x, y: b.y + b.height },
      ];
    }
    const out: { x: number; y: number }[] = [];
    for (let i = 0; i < count; i++) {
      const pt = path.getPointAtLength((total * i) / count);
      out.push({ x: pt.x, y: pt.y });
    }
    return out;
  }

  /** Bir katın parsel/rozet dolgu rengi: colorMode custom ise özel renk, değilse kat kategorisi. */
  function fillForCategory(loc: ApiLocation, cat: Category): string {
    if (loc.colorMode === "custom" && loc.customFill) return loc.customFill;
    return stylesByCategory.get(cat)?.fill ?? UNASSIGNED_FILL;
  }

  /** Tek bir kat+kategori rozeti: parselin merkezinde, o kiracının kategori rengiyle. */
  function addLevelBadge(loc: ApiLocation, li: BadgeInfo, center: { x: number; y: number }, size: number, fill: string) {
    const g = document.createElementNS(SVGNS, "g");
    g.setAttribute("class", "parcel-logo");
    g.dataset.id = loc.id;
    g.dataset.level = li.level;
    g.dataset.category = li.category; // tıklanınca doğru kiracı açılsın (aynı katta birden fazla olabilir)

    const disc = document.createElementNS(SVGNS, "circle");
    disc.setAttribute("cx", String(center.x));
    disc.setAttribute("cy", String(center.y));
    disc.setAttribute("r", String(size / 2));
    disc.setAttribute("fill", fill);          // kategori rengi = dış halka
    disc.setAttribute("stroke", "#ffffff");
    disc.setAttribute("stroke-width", String(Math.max(1, size * 0.08)));
    g.appendChild(disc);

    // Logo yüklüyse: beyaz iç daire + daireye kırpılmış logo (yazı yok)
    if (li.logo) {
      const inner = document.createElementNS(SVGNS, "circle");
      const ir = size / 2 * 0.86;
      inner.setAttribute("cx", String(center.x));
      inner.setAttribute("cy", String(center.y));
      inner.setAttribute("r", String(ir));
      inner.setAttribute("fill", "#ffffff");
      g.appendChild(inner);
      const img = document.createElementNS(SVGNS, "image");
      const s = ir * 2 * 0.92, x = center.x - s / 2, y = center.y - s / 2;
      img.setAttribute("x", String(x)); img.setAttribute("y", String(y));
      img.setAttribute("width", String(s)); img.setAttribute("height", String(s));
      img.setAttribute("preserveAspectRatio", "xMidYMid meet");
      img.setAttribute("clip-path", "url(#badge-logo-clip)");
      img.setAttribute("href", li.logo);
      // Logo yüklenemezse (kırık/kalıcı değil): logoyu kaldır, sade daire kalsın (soru işareti yok)
      img.addEventListener("error", () => { img.remove(); inner.remove(); });
      g.appendChild(img);
    }

    logoLayer.appendChild(g);
    // Rozette YAZI yok — logo varsa daire içinde logo, yoksa sade kategori rengi.
    return g;
  }

  // Rozet yerleşimi (SPEC): TEK circle logo, parselin TAM ortasında (centroid),
  // kata göre değişen logo/renk. TÜM circle'lar aynı boyut = en küçük rozet.
  // GRUPLAMA: loc.grouped ise lokasyonun tüm üye parselleri TEK rozetle (birleşik
  // merkez) gösterilir; grouped=false ise her üye parsele AYRI rozet.
  const filterTargets: FilterTargets = { parcels: [], seams: [], badgeSlots: [] };

  interface ParcelItem { p: SVGPathElement; center: { x: number; y: number }; size: number; }
  const byLocId = new Map<string, { loc: ApiLocation; items: ParcelItem[] }>();
  parcels.forEach((p) => {
    const parcelId = p.dataset.id;
    if (!parcelId) return;
    const loc = locByParcel.get(parcelId);
    if (!loc || !loc.units.length) return;

    p.setAttribute("fill", parcelFill(loc, stylesByCategory));
    p.dataset.locationId = loc.id;
    filterTargets.parcels.push({ loc, el: p });

    const geom = parcelGeom(samplePath(p, 28));
    const entry = byLocId.get(loc.id) ?? { loc, items: [] };
    entry.items.push({ p, center: geom.center, size: geom.size });
    byLocId.set(loc.id, entry);
  });

  // İÇ DİKİŞLER: `#dividers` içindeki her çizginin iki yanındaki parsel bulunur
  // (orta noktadan dik yönde yoklama). İkisi de AYNI lokasyona aitse çizgi o
  // lokasyonun içinde kalıyor demektir. Parseller ayraç genişliği kadar
  // boşlukla kesildiğinden çizgiyi gizlemek yetmez, altından taban görselin
  // rengi sızar; bu yüzden çizgi lokasyonun dolgu rengine boyanıp boşluğu
  // örtecek kadar kalınlaştırılır ve komşu parseller tek parça görünür.
  // Hangi dikişin kapanacağına yalnızca panelin atadığı lokasyon karar verir —
  // parsel kimliği koda gömülmez.
  const parcelAt = (pt: Pt): string | undefined => {
    for (const p of parcels) {
      if (p.dataset.id && p.isPointInFill(new DOMPoint(pt.x, pt.y))) return p.dataset.id;
    }
    return undefined;
  };
  svg.querySelectorAll<SVGPathElement>("#dividers path").forEach((line) => {
    const total = line.getTotalLength();
    if (!total) return;
    const a = line.getPointAtLength(0);
    const b = line.getPointAtLength(total);
    const [pa, pb] = dividerProbes({ x: a.x, y: a.y }, { x: b.x, y: b.y });
    const locA = locByParcel.get(parcelAt(pa) ?? "");
    const locB = locByParcel.get(parcelAt(pb) ?? "");
    if (!locA || !locB || locA.id !== locB.id || !locA.units.length) return;
    line.classList.add("divider-seam");
    line.setAttribute("stroke-width", String(SEAM_WIDTH));
    line.setAttribute("stroke-linecap", "butt"); // yuvarlak uç parselin dışına taşardı
    line.setAttribute("stroke", parcelFill(locA, stylesByCategory));
    filterTargets.seams.push({ loc: locA, el: line });
  });

  // Ortak boyut: tüm parseller arasındaki en küçük circle → hepsi aynı boyut.
  const allSizes = [...byLocId.values()].flatMap((e) => e.items.map((i) => i.size));
  const uniformSize = allSizes.length ? Math.min(...allSizes) : 16;

  for (const { loc, items } of byLocId.values()) {
    const infos = badgeInfos(loc);

    // Rozet "slot"ları: grouped → tek (birleşik merkez); değilse → parsel başına.
    const slots = (loc.grouped ?? true)
      ? [{
          center: {
            x: items.reduce((s, i) => s + i.center.x, 0) / items.length,
            y: items.reduce((s, i) => s + i.center.y, 0) / items.length,
          },
        }]
      : items.map((it) => ({ center: it.center }));

    for (const { center } of slots) {
      const badges: BadgeTarget[] = infos.map((li) => ({
        info: li,
        el: addLevelBadge(loc, li, center, uniformSize, fillForCategory(loc, li.category)),
      }));
      // Slot başına TEK rozet görünür. Varsayılan = en alt kat; hangisinin
      // görüneceğine filtre karar verir (aynı task içinde hemen düzeltir).
      // Hepsini gizli başlatmak, filtre herhangi bir sebeple çalışmazsa
      // haritayı rozetsiz bırakıyordu — bu yüzden ilki açık başlar.
      badges.forEach((b, i) => { if (i !== 0) b.el.classList.add("badge-hidden"); });
      filterTargets.badgeSlots.push({ badges });
    }
  }

  // NOT: Burada eskiden sabit bir "car park zonu" parsel listesi vardı; koda
  // gömülü olduğu için panelde o parseller başka lokasyonlara atandığında
  // bayatlıyor ve haritayı yanlış boyuyordu. Kaldırıldı — hangi parselin ne
  // olduğuna YALNIZCA panel karar verir. Kiracısız bir alanı boyamak için
  // panelden normal bir lokasyon açılır (parsel + "Extra parcels" + bir ünite).

  // NOT: Burada eskiden, parseli olmayan bir "marina" lokasyonu için SABİT
  // piksel koordinatına (1150, 445) bir işaretçi çizen bir yedek yol vardı.
  // Kaldırıldı: haritadaki her şeyin yeri panelden atanan parselden gelir.
  // Bir lokasyon haritada görünsün isteniyorsa panelden parsel atanır.


  const bv = svg.viewBox.baseVal;
  const fullViewBox: ViewBoxRect = { x: bv.x, y: bv.y, width: bv.width, height: bv.height };

  // Başlangıç görünümü: marinaya odaklan (boş denizi gösterme). Parsellerin
  // sınır kutusunu al, üstte su/tekneler görünsün diye yukarı doğru genişlet.
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  parcels.forEach((p) => {
    const b = p.getBBox();
    if (b.x < minx) minx = b.x;
    if (b.y < miny) miny = b.y;
    if (b.x + b.width > maxx) maxx = b.x + b.width;
    if (b.y + b.height > maxy) maxy = b.y + b.height;
  });
  let bounds: ViewBoxRect = fullViewBox;
  if (isFinite(minx)) {
    const cw = container.clientWidth || 1280, ch = container.clientHeight || 800;
    const A = cw / ch;
    const w = maxx - minx, h = maxy - miny;

    // Gezilebilir sınır: altta kara (parsellerin biraz altı), üstte havuz +
    // sınırlı deniz, harita dışına taşmaz → sonsuz scroll / boş deniz yok.
    const left = Math.max(fullViewBox.x, minx - w * 0.03);
    const right = Math.min(fullViewBox.x + fullViewBox.width, maxx + w * 0.03);
    const top = Math.max(fullViewBox.y, miny - h * 1.3);
    const bottom = Math.min(fullViewBox.y + fullViewBox.height, maxy + h * 1.0);
    bounds = { x: left, y: top, width: right - left, height: bottom - top };

    // Başlangıç görünümü: marinaya odaklı, alt kenar karada. En uzak zoom'un
    // (boşluksuz sınır) %78'i → zoom-OUT için pay kalır (sınırda takılmaz).
    const maxW = Math.min(bounds.width, bounds.height * A);
    let vbw = maxW * 0.78;
    let vbh = vbw / A;
    bv.width = vbw;
    bv.height = vbh;
    bv.x = (minx + maxx) / 2 - vbw / 2;
    bv.y = bottom - vbh; // alt kenar = kara
  }

  return {
    svg,
    parcelCount: parcels.length,
    getLocation: (id) => locById.get(id),
    bounds,
    filterTargets,
  };
}
